import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, PencilSimple, Phone, Envelope, MapPin } from "@phosphor-icons/react";
import { FilterSearchBar } from "@/components/doctor/FilterBar";

const EMPTY = { name: "", contact_name: "", email: "", phone: "", address: "", gstin: "", payment_terms_days: 30, notes: "" };


export function VendorsTab({ onChange }) {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const load = () => api.get("/admin/inventory/vendors").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (!q.trim()) return items;
        const ql = q.toLowerCase();
        return items.filter((v) =>
            (v.name || "").toLowerCase().includes(ql) ||
            (v.email || "").toLowerCase().includes(ql) ||
            (v.contact_name || "").toLowerCase().includes(ql)
        );
    }, [items, q]);

    const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
    const openEdit = (v) => { setEditing(v); setForm({ ...EMPTY, ...v }); setOpen(true); };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Vendor name required"); return; }
        try {
            const payload = { ...form, payment_terms_days: Number(form.payment_terms_days) || 30 };
            if (editing) {
                await api.put(`/admin/inventory/vendors/${editing.id}`, payload);
                toast.success("Vendor updated");
            } else {
                await api.post("/admin/inventory/vendors", payload);
                toast.success("Vendor added");
            }
            setOpen(false); load(); onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="space-y-4" data-testid="vendors-tab">
            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search vendor name, contact, email…"
                searchTestid="vendors-search"
                testid="vendors-search-bar"
                rightSlot={(
                    <Button size="sm" className="rounded-full" onClick={openCreate} data-testid="vendors-add">
                        <Plus size={14} weight="bold" className="mr-1.5" /> Add Vendor
                    </Button>
                )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.length === 0 ? (
                    <div className="col-span-full bg-card border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground text-sm" data-testid="vendors-empty">
                        {items.length === 0 ? "No vendors yet. Add your first supplier to begin issuing purchase orders." : "No vendors match the search."}
                    </div>
                ) : filtered.map((v) => (
                    <div key={v.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`vendor-card-${v.id}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold text-sm truncate" title={v.name}>{v.name}</div>
                                {v.contact_name && <div className="text-[11px] text-muted-foreground truncate">{v.contact_name}</div>}
                            </div>
                            <button type="button" onClick={() => openEdit(v)} className="p-1 hover:bg-secondary rounded shrink-0" data-testid={`vendor-edit-${v.id}`}>
                                <PencilSimple size={13} className="text-muted-foreground" />
                            </button>
                        </div>
                        <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                            {v.phone && <div className="flex items-center gap-1.5 truncate"><Phone size={11} /> {v.phone}</div>}
                            {v.email && <div className="flex items-center gap-1.5 truncate"><Envelope size={11} /> {v.email}</div>}
                            {v.address && <div className="flex items-start gap-1.5 truncate"><MapPin size={11} className="mt-0.5 shrink-0" /> <span className="truncate">{v.address}</span></div>}
                        </div>
                        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-[11px]">
                            <span className="text-muted-foreground">Net {v.payment_terms_days || 30} days</span>
                            {v.gstin && <span className="font-mono text-[10px] text-muted-foreground">{v.gstin}</span>}
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="vendor-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
                        <DialogDescription>Supplier of resins / TPU / IPA / packaging etc.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><Label>Vendor name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" /></div>
                        <div><Label>Contact person</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="vendor-contact" /></div>
                        <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vendor-phone" /></div>
                        <div className="col-span-2"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vendor-email" /></div>
                        <div className="col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} data-testid="vendor-address" /></div>
                        <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="font-mono" data-testid="vendor-gstin" /></div>
                        <div><Label>Payment terms (days)</Label><Input type="number" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} data-testid="vendor-terms" /></div>
                        <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="vendor-notes" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="vendor-save">{editing ? "Save" : "Add"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
